/**
 * Turn a failed candidate stage move into a sentence for the Move Stage
 * dialog. Machine codes such as no_job_link stay off the screen.
 */
import { extractApiError } from './apiErrors';

type AxiosLike = {
  response?: { data?: { error?: string; message?: string; details?: unknown } };
};

export interface MoveStageFailure {
  message: string;
  /** True when the move was refused because the candidate has no job. */
  needsJob: boolean;
}

export function describeMoveStageFailure(err: unknown, stageLabel?: string): MoveStageFailure {
  const data = (err as AxiosLike)?.response?.data;
  const code = typeof data?.error === 'string' ? data.error : '';
  const label = stageLabel?.trim() || 'this stage';

  if (code === 'no_job_link') {
    return {
      needsJob: true,
      message: `This candidate needs to be linked to a job before moving to ${label}. Link a job first.`,
    };
  }
  if (code === 'Unauthorized') {
    return { needsJob: false, message: 'Sign in again, then retry this move.' };
  }
  if (code === 'Forbidden') {
    return { needsJob: false, message: 'You do not have permission to move this candidate.' };
  }
  if (code === 'Candidate not found') {
    return { needsJob: false, message: 'This candidate could not be found. Refresh the page and try again.' };
  }
  if (code === 'Failed to move stage' || code === 'Internal Server Error') {
    return { needsJob: false, message: 'The stage move did not save. Try again.' };
  }
  if (/^Unknown stage\b/i.test(code)) {
    return { needsJob: false, message: 'That stage is not available. Choose a stage from the list.' };
  }

  const text = extractApiError(err, 'The stage move did not save. Try again.');
  if (text === 'Validation error') {
    return { needsJob: false, message: 'Check the stage and notes, then try again.' };
  }
  if (/^[a-z0-9_]+$/.test(text)) {
    return { needsJob: false, message: 'The stage move did not save. Try again.' };
  }
  return { needsJob: false, message: text };
}
